import Vue from 'vue';
import { mapGetters } from 'vuex';
import Dashboard from '@/components/Dashboard';
import Mobile from '@/components/Mobile';
import Leads from '@/pages/Leads/Leads';
import Search from '@/pages/Search/Search';
import Tickets from '@/pages/Tickets/Tickets';
import LeadDetails from '@/pages/Leads/LeadDetails';
import LeadMobile from '@/pages/Leads/LeadMobile';
import Users from '@/pages/Users/Users';
import Login from '@/pages/Login/Login';
import Campaigns from '@/pages/Campaigns/Campaigns';
import Companies from '@/pages/Companies/Companies';
import CompanyDetails from '@/pages/Companies/CompanyDetails';
import CampaignDetails from '@/pages/Campaigns/Details/CampaignDetails';
import NotFound from '@/pages/NotFound/NotFound';
import Guide from '@/pages/Guide/Guide';
import Lead from '@/modals/LeadModal';
import AssignCampaign from '@/modals/AssignCampaign';
import AssignUser from '@/modals/AssignUser';
import AddUser from '@/modals/AddUser';
import CreatePaymentModal from '@/modals/CreatePaymentModal';
import AddTransaction from '@/modals/AddTransactionModal';
import ChatModal from '@/modals/Chat';
import EditCompany from '@/modals/EditCompanyModal';
import EditCampaign from '@/modals/EditCampaignModal';
import EditClientCampaign from '@/modals/EditClientCampaignModal';
import EditShopifyCampaign from '@/modals/EditShopifyCampaignModal';
import AddCard from '@/modals/AddCardModal';
import EditFieldAndQuery from '@/modals/FieldAndQuery/EditFieldAndQueryModal';
import UserPhoto from '@/modals/UserPhotoModal';
import FileModal from '@/modals/File';
import NewQuestionsModal from '@/modals/NewQuestions';
import CloneCampaignModal from '@/modals/CloneCampaignModal';
import LeadRefundModal from '@/modals/LeadRefundModal';
import CustomRouter from '@/helpers/Router';
import Agents from '@/pages/Reports/Agents';
import Signup from '@/pages/Signup/Signup';

import store from '../store';

Vue.use(CustomRouter);

export default new CustomRouter({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'Dashboard',
      component: Dashboard,
      beforeEnter: (to, from, next) => {
        if (store.state.auth) {
          const args = to.fullPath === '/' || to === '/' ? ['/leads'] : [];
          next(...args);
        } else {
          next(to === '/signup' ? to : '/login');
        }
      },
      meta: {
        grants: ['*'],
      },
      children: [
        {
          path: '/leads',
          name: 'Leads',
          component: Leads,
          meta: {
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Leads', text: 'Leads' },
            ],
            grants: ['leads.find'],
          },
          children: [
            {
              path: 'json-detail/:id',
              name: 'Lead',
              component: Lead,
              meta: {
                breadcrumbs: [
                  { name: 'Dashboard', text: 'Home' },
                  { name: 'Leads', text: 'Leads' },
                  { name: 'Lead', text: 'Lead' },
                ],
                grants: ['leads.find'],
              },
            },
          ],
        },
        {
          path: '/search/lead',
          name: 'SearchLead',
          component: Search,
          meta: {
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Leads', text: 'Leads' },
              { name: 'SearchLead', text: 'Search' },
            ],
            grants: ['leads.search'],
          },
        },
        {
          path: '/leads/:id',
          name: 'LeadDetails',
          component: LeadDetails,
          meta: {
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Leads', text: 'Leads' },
            ],
            grants: ['leads.find'],
          },
        },
        {
          path: '/leads/:id/refund',
          name: 'LeadRefund',
          components: { default: LeadDetails, modal: LeadRefundModal },
          meta: {
            grants: ['companies.update'],
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Leads', text: 'Leads' },
            ],
          },
          props: { default: true, modal: true },
        },
        {
          path: '/tickets',
          name: 'Tickets',
          component: Tickets,
          meta: {
            grants: ['tickets.find'],
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Tickets', text: 'Tckets' },
            ],
          },
        },
        {
          path: '/users',
          name: 'Users',
          component: Users,
          meta: {
            grants: ['users.find'],
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Users', text: 'Users' },
            ],
          },
        },
        {
          path: '/users/photo/:id',
          name: 'UserPhoto',
          components: { default: Users, modal: UserPhoto },
          meta: {
            grants: ['users.find'],
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Users', text: 'Users' },
            ],
          },
          props: { default: false, modal: true },
        },
        {
          path: '/campaigns/:id/clone/:name',
          name: 'CloneCampaign',
          components: { default: CampaignDetails, modal: CloneCampaignModal },
          meta: {
            grants: ['campaigns.create'],
          },
          props: {
            default: false,
            modal: route => ({
              campaignId: +route.params.id,
              campaignName: route.params.name,
            }),
          },
        },
        {
          path: '/campaigns/:id/questions/new',
          name: 'NewCampaignQuestion',
          components: { default: CampaignDetails, modal: NewQuestionsModal },
          meta: {
            grants: ['*'],
          },
          props: {
            default: false,
            modal: route => ({
              campaignId: +route.params.id,
            }),
          },
        },
        {
          path: '/campaigns/:id/questions/edit/:questionId',
          name: 'EditCampaignQuestion',
          components: { default: CampaignDetails, modal: NewQuestionsModal },
          meta: {
            grants: ['*'],
          },
          props: {
            default: false,
            modal: route => ({
              questionId: +route.params.questionId,
              campaignId: +route.params.id,
              question: route.query.question,
              answer: route.query.answer,
            }),
          },
        },
        {
          path: '/campaigns/:id/file',
          name: 'FileModal',
          components: { default: CampaignDetails, modal: FileModal },
          meta: {
            grants: ['*'],
          },
          props: { default: false, modal: true },
        },
        {
          path: '/users/add',
          name: 'AddUser',
          components: { default: Users, modal: AddUser },
          meta: {
            grants: ['users.create'],
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Users', text: 'Users' },
            ],
          },
        },
        {
          path: '/messages/:ticketId',
          name: 'Messages',
          components: { default: Tickets, modal: ChatModal },
          meta: {
            grants: ['tickets.messages'],
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Tickets', text: 'Tickets' },
            ],
          },
        },
        {
          path: '/users/update/:userId',
          name: 'UpdateUser',
          components: { default: Users, modal: AddUser },
          meta: {
            grants: ['users.update'],
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Users', text: 'Users' },
            ],
          },
          props: {
            default: false,
            modal: route => ({
              userId: +route.params.userId,
            }),
          },
        },
        {
          path: '/users/:userId/campaigns',
          name: 'AssignCampaigns',
          components: { default: Users, modal: AssignCampaign },
          meta: {
            grants: ['users.update', 'users.create'],
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Users', text: 'Users' },
            ],
          },
          props: {
            default: false,
            modal: route => ({
              userId: +route.params.userId,
            }),
          },
        },
        {
          path: '/campaigns/assign/:campaignId',
          name: 'AssignUsers',
          components: { default: Campaigns, modal: AssignUser },
          meta: {
            grants: ['campaigns.update', 'campaigns.create'],
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Campaigns', text: 'Campaigns' },
            ],
          },
          props: {
            default: false,
            modal: route => ({
              campaignId: +route.params.campaignId,
              companyId: +route.query.companyId,
            }),
          },
        },
        {
          path: '/campaigns',
          name: 'Campaigns',
          component: Campaigns,
          meta: {
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Campaigns', text: 'Campaigns' },
            ],
            grants: ['campaigns.find'],
          },
        },
        {
          path: '/guides/:id',
          name: 'Guides',
          component: Guide,
          meta: {
            grants: ['*'],
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Guides', text: 'Guides' },
            ],
          },
          props: true,
        },
        {
          path: '/campaigns/new',
          name: 'NewCampaign',
          components: {
            default: Campaigns,
            modal: {
              components: { EditCampaign, EditClientCampaign },
              computed: { ...mapGetters(['getAuthUser']) },
              render(h) {
                const props = {
                  attrs: this.$attrs,
                  props: this.$props,
                  slots: this.$slots,
                  on: this.$listeners,
                };
                return this.getAuthUser.role.role_type === 'administrator'
                  ? h('edit-campaign', props)
                  : h('edit-client-campaign', props);
              },
            },
          },
          meta: {
            grants: ['campaigns.create'],
            breadcrumbs: [{ name: 'Campaigns', text: 'Campaigns' }],
          },
          props: { default: false, modal: true },
        },
        {
          path: '/campaigns/new-shopify',
          name: 'NewShopifyCampaign',
          components: {
            default: Campaigns,
            modal: {
              components: { EditShopifyCampaign },
              computed: { ...mapGetters(['getAuthUser']) },
              render(h) {
                const props = {
                  attrs: this.$attrs,
                  props: this.$props,
                  slots: this.$slots,
                  on: this.$listeners,
                };
                return this.getAuthUser.role.role_type === 'administrator'
                  ? h('edit-shopify-campaign', props)
                  : null;
              },
            },
          },
          meta: {
            grants: ['campaigns.create'],
            breadcrumbs: [{ name: 'Campaigns', text: 'Campaigns' }],
          },
          props: { default: false, modal: true },
        },
        {
          path: '/campaigns/edit/:id',
          name: 'EditCampaign',
          components: { default: Campaigns, modal: EditCampaign },
          meta: {
            grants: ['campaigns.update'],
            breadcrumbs: [{ name: 'Campaigns', text: 'Campaigns' }],
          },
          props: { default: false, modal: true },
        },
        {
          path: '/campaigns/edit-shopify/:id',
          name: 'EditShopifyCampaign',
          components: { default: Campaigns, modal: EditShopifyCampaign },
          meta: {
            grants: ['campaigns.update'],
            breadcrumbs: [{ name: 'Campaigns', text: 'Campaigns' }],
          },
          props: { default: false, modal: true },
        },
        {
          path: '/questions/:id/new',
          name: 'NewQuestion',
          components: { default: CampaignDetails, modal: EditFieldAndQuery },
          meta: {
            grants: ['questions.create'],
            breadcrumbs: [{ name: 'Campaigns', text: 'Campaigns' }],
          },
          props: { default: false, modal: true },
        },
        {
          path: '/questions/:id/edit/:questionId',
          name: 'EditQuestion',
          components: { default: CampaignDetails, modal: EditFieldAndQuery },
          meta: {
            grants: ['questions.update'],
            breadcrumbs: [{ name: 'Campaigns', text: 'Campaigns' }],
          },
          props: { default: false, modal: true },
        },
        {
          path: '/campaigns/:id',
          name: 'CampaignDetails',
          component: CampaignDetails,
          meta: {
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Campaigns', text: 'Campaigns' },
            ],
            grants: ['campaigns.details'],
          },
        },
        {
          path: '/companies',
          name: 'Companies',
          component: Companies,
          meta: {
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Companies', text: 'Companies' },
            ],
            grants: ['companies.find'],
          },
        },
        {
          path: '/companies/new',
          name: 'NewCompany',
          components: { default: Companies, modal: EditCompany },
          meta: {
            grants: ['companies.create'],
            breadcrumbs: [{ name: 'Companies', text: 'Companies' }],
          },
          props: { default: false, modal: true },
        },
        {
          path: '/companies/edit/:id',
          name: 'EditCompany',
          component: CompanyDetails,
          meta: {
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Companies', text: 'Companies' },
            ],
            grants: ['companies.update'],
          },
          beforeEnter: (to, from, next) => {
            const {
              role: { role_type: roleType },
              companyId,
            } = store.getters.getAuthUser;

            // check authentication
            if (roleType === 'administrator' || companyId) {
              next();
            } else {
              next('/');
            }
          },
        },
        {
          path: '/companies/edit/:id/transaction/new',
          name: 'NewTransaction',
          components: { default: CompanyDetails, modal: AddTransaction },
          meta: {
            grants: ['companies.update'],
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Companies', text: 'Companies' },
              { name: 'EditCompany', text: 'Company' },
            ],
          },
          props: { default: false, modal: true },
        },
        {
          path: '/companies/edit/:id/payment/new',
          name: 'NewPayment',
          components: { default: CompanyDetails, modal: CreatePaymentModal },
          meta: {
            grants: ['companies.createPayment'],
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Companies', text: 'Companies' },
              { name: 'EditCompany', text: 'Company' },
            ],
          },
          props: { default: false, modal: true },
        },
        {
          path: '/reports/agents',
          name: 'Agents',
          component: Agents,
          meta: {
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Agents', text: 'Agents' },
            ],
            grants: ['companies.find'],
          },
        },
        {
          path: '/companies/edit/:id/billing/new',
          name: 'NewCard',
          components: { default: CompanyDetails, modal: AddCard },
          meta: {
            grants: ['companies.update'],
            breadcrumbs: [
              { name: 'Dashboard', text: 'Home' },
              { name: 'Companies', text: 'Companies' },
              { name: 'EditCompany', text: 'Company' },
            ],
          },
          props: { default: false, modal: true },
        },
      ],
    },
    {
      path: '/m',
      name: 'Mobile',
      component: Mobile,
      meta: {
        grants: ['*'],
      },
      children: [
        {
          path: 'lead/:token',
          name: 'Lead',
          component: LeadMobile,
          meta: {
            grants: ['*'],
          },
        },
      ],
    },
    {
      path: '/login',
      name: 'Login',
      component: Login,
      meta: {
        grants: ['*'],
      },
    },
    {
      path: '/signup',
      name: 'Signup',
      component: Signup,
      meta: {
        grants: ['*'],
      },
    },
    {
      path: '/notfound',
      name: 'NotFound',
      component: NotFound,
      meta: {
        grants: ['*'],
      },
    },
  ],
});



// WEBPACK FOOTER //
// ./src/router/index.js